import { Linkify } from './RuleText.js';
import { isManager, managerShort } from '../domain/identity.js';
/**
 * Past drafts — 23 seasons, 3,756 picks, out of the archive workbook.
 *
 * Two ways to look at a draft, because people ask two different questions. "What went where in
 * round 3 of 2019?" wants the board. "What has Rajan ever drafted in the first?" wants a team
 * column. Both render off the same array.
 *
 * The data carries its own caveats — seasons before 2016 have no keeper markers, seasons before
 * 2009 have no per-pick GM — and those are shown rather than smoothed over. A history that
 * quietly implies it knows more than it does is worse than one with gaps in it.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import history from '../data/draft-history.json';
import { matcher, nameKey } from '../domain/names.js';
import { nameFit, splitName } from './BoardGrid.js';
import { NflChip } from './NflChip.js';
import { hueFor } from './GmBadge.js';
import { historyInfoOfPick } from '../domain/historyClubs.js';

/**
 * "Show me this pick."
 *
 * Sent by another screen — today the keeper history, where every season chip is a link to the
 * draft it came from. Carried as season + player rather than an overall pick number because the
 * sender does not have one: the keeper archive records the round a player was kept at, and the
 * pick within that round is the draft board's own business.
 */
export interface DraftFocus { season: number; player: string }

interface Pick {
  overall: number; round: number; pick: number; gm: string; player: string;
  keeper?: boolean; farm?: boolean; gmResolved?: boolean;
  /**
   * HELD UNDER THE FRANCHISE TAG. The archive workbook has no marker for one — its whole
   * vocabulary is `+` for a keeper and `°` for a farm graduate — so the flag is recorded by hand
   * from the league's own declarations. The rule came in for 2025 and two men were tagged that
   * year; see `conventions.franchise` in `draft-history.json`.
   */
  franchise?: boolean;
}
interface Season {
  season: number; teams: number; rounds: number; order: string[]; picks: Pick[];
  partial?: boolean; note?: string; keepersUnknown?: boolean;
  pickOrderInferred?: boolean; gmFromSeatOnly?: boolean;
  /**
   * The era's own fantasy team names, in seat order — `Okra Patch Runners`, `'roid rage`. Added
   * with the Yahoo merge (8 September): a board from 2006 is a board of team names, and half of
   * them belong to nobody the roster can name.
   */
  teamNames?: Array<string | null>;
  /** Seats Yahoo will not name — it hides manager names before 2009 — and the league has not. */
  gmSeatsUnnamed?: string[];
  /** The source marks no keepers at all this season, which is not the same as not knowing. */
  keeperMarksNone?: boolean;
}

const DATA = history as unknown as { source: string; readOn: string; seasons: Season[] };
const SEASONS = DATA.seasons;

export function DraftHistory({ focus }: { focus?: DraftFocus }) {
  const [year, setYear] = useState(focus?.season ?? SEASONS[0]!.season);
  const [view, setView] = useState<'board' | 'team'>('board');
  const [q, setQ] = useState('');
  /** The overall pick number to light up, or null. Cleared the moment the reader steers. */
  const [lit, setLit] = useState<number | null>(null);
  const litCell = useRef<HTMLDivElement | null>(null);

  const season = SEASONS.find((s) => s.season === year)!;

  /**
   * Arriving from somewhere else with a pick to show.
   *
   * The sender knows the season and the player, so the pick is resolved here, against this
   * season's board. Matching is on the normalised name — the two archives were transcribed years
   * apart and disagree about full stops and suffixes — and if nothing matches, the season still
   * opens. Landing on the right draft with nothing highlighted is a mild disappointment; throwing
   * away the navigation because a name has a Jr. in it is a broken link.
   */
  useEffect(() => {
    if (!focus) return;
    setYear(focus.season);
    setView('board');
    setQ('');
    const s = SEASONS.find((x) => x.season === focus.season);
    const want = nameKey(focus.player);
    setLit(s?.picks.find((p) => p.player && nameKey(p.player) === want)?.overall ?? null);
  }, [focus]);

  // Scrolled after paint, because the cell does not exist until the board has rendered.
  useEffect(() => {
    if (lit !== null) litCell.current?.scrollIntoView({ block: 'center', inline: 'center' });
  }, [lit, year, view]);

  /**
   * OPEN THE BOARD ON THIS PICK (Tim, 8 September: "clicking a result from the past draft history
   * should take me to that draft").
   *
   * The search runs across twenty-three seasons and its whole job is to find where a man went —
   * and then it left the reader to notice the year, clear the box and find the cell himself. Same
   * landing as an arrival from anywhere else in the app: the season, the board, and the pick lit
   * and scrolled to.
   */
  const openPick = (s: number, overall: number) => {
    setQ('');
    setYear(s);
    setView('board');
    setLit(overall);
  };

  /** Any deliberate move by the reader retires the highlight — it was an arrival, not a filter. */
  const steer = (fn: () => void) => {
    setLit(null);
    fn();
  };

  /** Search runs across every season — "who has ever drafted Gronkowski" is the real question. */
  const hits = useMemo(() => {
    // `%` is a wildcard, so "zac% miller" catches Zach and Zack in one go.
    if (q.trim().replace(/%/g, '').length < 3) return null;
    const match = matcher(q);
    const out: Array<Pick & { season: number }> = [];
    for (const s of SEASONS) {
      for (const p of s.picks) {
        if (p.player && match(p.player)) out.push({ ...p, season: s.season });
      }
    }
    return out.sort((a, b) => b.season - a.season || a.overall - b.overall).slice(0, 200);
  }, [q]);

  const byGm = useMemo(() => {
    const m = new Map<string, Pick[]>();
    for (const g of season.order) m.set(g, []);
    for (const p of season.picks) {
      if (!m.has(p.gm)) m.set(p.gm, []);
      m.get(p.gm)!.push(p);
    }
    return [...m.entries()];
  }, [season]);

  /* 17l: the archive's own words for what it does not know, as chips on the title line. */
  /*
   * AND THE CLUBS IT COULD NOT NAME. `history-clubs.json` resolves about ninety-six picks in a
   * hundred; the rest are mostly men who were drafted and never started, so no lineup in the
   * league's own archive says what they were wearing. The cell draws no chip and the head says
   * how many, rather than the page implying it knows every one of them.
   */
  /*
   * WHICH CLUBS, NOT HOW MANY (Tim, 8 September, of 2014, 2012 and 2011: "says '2 clubs unknown'
   * when all clubs that year are known"). They were not — Ray Rice sat out 2014 and Matt Prater
   * was suspended, so no lineup in the league's own archive says what either was wearing — but a
   * bare count over a hundred and sixty cells is a claim the reader has to go and check by eye,
   * and two missing chips on a board this size are not findable. It names them now.
   *
   * Counted off the picks rather than off the club file's own row count, which is one row per
   * NAME: 2010 drafted two Mike Williamses and the count came out one too high.
   */
  const noClubNames = season.picks
    .filter((p) => p.player && !historyInfoOfPick(season.season, p.round, p.pick, p.player).club)
    .map((p) => p.player);
  const noClub = noClubNames.length;
  /*
   * WHAT THIS SEASON DOES NOT KNOW, in its own words. Three of these changed with the Yahoo merge
   * (8 September): the GM on a pick is the team that MADE it now rather than the seat that owned
   * it, so `GM is the seat` is gone and what is left to say is which seats nobody can name; the
   * round and pick are Yahoo's own sequence rather than an inferred snake; and a season the source
   * marks no keepers in says that, rather than saying it does not know.
   */
  const unnamed = season.gmSeatsUnnamed ?? [];
  const caveats = [
    unnamed.length > 0
      ? `${unnamed.length} seat${unnamed.length === 1 ? '' : 's'} unnamed: ${unnamed.join(', ')}`
      : null,
    season.gmFromSeatOnly ? 'GM is the seat' : null,
    season.keepersUnknown ? 'keeper marks unknown' : null,
    season.keeperMarksNone ? 'no keeper marks in either source' : null,
    season.pickOrderInferred ? 'snake order inferred' : null,
    season.partial ? (season.note ?? 'partial') : null,
    noClub > 0
      ? `${noClub} club${noClub === 1 ? '' : 's'} unknown: ${noClubNames.slice(0, 4).join(', ')}`
        + (noClub > 4 ? ` and ${noClub - 4} more` : '')
      : null,
  ].filter((x): x is string => Boolean(x));
  /** The column head: a seat number where the archive knows only the seat, else the GM. */
  const headFor = (g: string, i: number) => (season.gmFromSeatOnly ? `Seat ${i + 1}` : managerShort(g));
  /** The team he ran that year, under his name — the thing the league actually called that seat. */
  const teamFor = (i: number) => season.teamNames?.[i] ?? null;
  const seatOf = (g: string) => {
    const i = season.order.indexOf(g);
    return i >= 0 ? `Seat ${i + 1}` : g;
  };
  /**
   * A COLOUR PER TEAM ON THIS BOARD (Tim, 8 September: "ensure all past draftboards have distinct
   * colors for each picking team (and whatever traded picks they picked from)").
   *
   * `hueFor` reads the roster's own fixed index, which is what keeps Rajan the same colour on
   * every board in the app — and it has one answer for everybody the roster has never heard of.
   * The older seasons record a SEAT LABEL rather than a manager (`ROID RAGE`, `TEXAS HOLD 'EM`),
   * so seven of the ten teams on the 2003 board shared one tint and the board read as three
   * managers and a crowd.
   *
   * So: the roster's hue for a manager, and for a label, the next golden-angle slot this season
   * has not already spent. It is derived from the season's own seat list, which is fixed, so a
   * board's colours do not move between visits — and it stays out of the way of the real
   * managers' hues, which is what makes a man recognisable across twenty-three boards.
   */
  const hues = useMemo(() => {
    const out = new Map<string, number>();
    const used = new Set<number>();
    /*
     * Keyed upper-case: the workbook is not consistent about the case of a seat label, and 2003
     * carries both `WAIVER WIRE GOONS` in its seat list and `Waiver Wire Goons` on two of its
     * picks. Matching literally gave that one team two colours on its own board.
     */
    for (const g of season.order) {
      if (!isManager(g)) continue;
      const h = hueFor(g);
      out.set(g.toUpperCase(), h);
      used.add(h);
    }
    let n = 0;
    for (const g of season.order) {
      if (out.has(g.toUpperCase())) continue;
      let h = Math.round((n * 137.5) % 360);
      while (used.has(h)) { n += 1; h = Math.round((n * 137.5) % 360); }
      n += 1;
      out.set(g.toUpperCase(), h);
      used.add(h);
    }
    return out;
  }, [season]);
  /* A pick made from a traded slot takes the colour of the man who MADE it, not the seat's. */
  const hueOf = (gm: string) => hues.get(gm.toUpperCase()) ?? hueFor(gm);

  const code = (p: Pick) => `${p.round}.${String(p.pick).padStart(2, '0')}`;
  const [round, setRound] = useState(1);
  useEffect(() => { setRound(lit ? (season.picks.find((p) => p.overall === lit)?.round ?? 1) : 1); }, [year, lit, season]);

  /*
   * THE ROW'S NAME, with the era's own position beside it. It used to read the position off
   * TODAY's player index, which answers for the men still playing and says nothing at all about
   * a 2007 tight end — see `historyClubs.ts` for why the club is never read from there.
   */
  const name = (p: Pick) => {
    const player = p.player;
    if (!player) return <span className="hname"><b>—</b></span>;
    const { last, first } = splitName(player);
    const { pos, club } = historyInfoOfPick(year, p.round, p.pick, player);
    return (
      <span className="hname" title={player}>
        <b>{last}</b>{first ? <i> {first}</i> : null}
        {pos && <span className={`poschip ident pos-${pos}`}>{pos}</span>}
        {club && <NflChip team={club} title={`${club} in ${year}`} />}
      </span>
    );
  };

  /*
   * THE CELL IS THE LIVE BOARD'S CELL (Tim, 7 September: "update past drafts to look more like
   * the pick design we've come up with for the live draft").
   *
   * Same markup, same class names — `.dbcell.made` with its `.top` name block, its `.bot` strip
   * of position, club and pick code, and the corner `K` — because this page already renders
   * inside `.app.draftpg` and the record and the night should not be two different objects. What
   * it adds is the club he was ON that year, which the live board takes from the player index and
   * a 2009 board cannot.
   */
  const cell = (p: Pick) => {
    const { last, first } = splitName(p.player);
    const { pos, club } = historyInfoOfPick(year, p.round, p.pick, p.player);
    return (
      <>
        <span className="top">
          <span className={`pn ${nameFit(last)}`} title={p.player}>
            <b>{last}</b>
            {first ? <span className="pnsub"><i>{first}</i></span> : null}
          </span>
        </span>
        <span className="bot">
          {pos && <span className={`poschip ident pos-${pos}`}>{pos}</span>}
          {club && <NflChip team={club} title={`${club} in ${year}`} />}
          <span className="code">{code(p)}</span>
        </span>
        {/*
          THREE BADGES, THE BOARD'S OWN THREE. A tag is a different fact from a keeper — it is
          the one band-limited right in the rulebook, it costs three keeper slots, and the live
          board has drawn it as a green `T` since 17a. The archive drew every held man as a `K`,
          so a season's two most expensive decisions were invisible in the record of it.
        */}
        {p.keeper && (
          <span className={`kbadge ${p.franchise ? 't' : p.farm ? 'f' : 'k'}`} aria-hidden="true"
            title={p.franchise ? 'Held under the franchise tag'
              : p.farm ? 'Farm graduate' : 'Keeper'}>
            {p.franchise ? 'T' : p.farm ? 'F' : 'K'}
          </span>
        )}
      </>
    );
  };

  return (
    <div className="app wide draftpg histpg">
      <Linkify>
        <header className="pagehead">
          <div>
            <h1>Past drafts</h1>
            <p className="sub">
              {SEASONS.length} seasons, {SEASONS.reduce((n, s) => n + s.picks.length, 0).toLocaleString()} picks,
              read out of the Draftboard archive workbook.
            </p>
          </div>
          <input className="search" type="search" placeholder="Search every season — % is a wildcard"
            value={q} onChange={(e) => setQ(e.target.value)} />
        </header>

        {hits ? (
          <section className="card">
            <div className="striptop">
              <h3 className="striphead">{hits.length} matches for &ldquo;{q.trim()}&rdquo;</h3>
              <button type="button" className="linkbtn" onClick={() => setQ('')}>clear</button>
            </div>
            <table>
              <thead>
                <tr><th>Season</th><th>Pick</th><th>Player</th><th>GM</th><th /></tr>
              </thead>
              <tbody>
                {hits.map((p) => {
                  /*
                   * THE ERA'S OWN POSITION AND CLUB (Tim, 8 September: "past draft search should
                   * also return position and NFL team"). Read for the hit's OWN season rather than
                   * the one on screen — a search runs across twenty-three of them, and a man who
                   * appears in four was not on the same club in all four. Where the archive cannot
                   * name one, nothing is drawn: see the caveat line on the board below.
                   */
                  const { pos, club } = historyInfoOfPick(p.season, p.round, p.pick, p.player);
                  return (
                  /* THE WHOLE ROW IS THE LINK — it is a search result, and there is nothing else
                     on it to want. Keyboard reachable, because a table row is not a button. */
                  <tr key={`${p.season}.${p.overall}`} className="hitrow" tabIndex={0}
                    role="link"
                    aria-label={`${p.player} — ${p.season} round ${p.round} pick ${p.pick}. Open the board`}
                    onClick={() => openPick(p.season, p.overall)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openPick(p.season, p.overall);
                      }
                    }}>
                    <td className="strong">{p.season}</td>
                    <td className="mono">{p.round}.{String(p.pick).padStart(2, '0')}</td>
                    <td>
                      <span className="hname">
                        {p.player}
                        {pos && <span className={`poschip ident pos-${pos}`}>{pos}</span>}
                        {club && <NflChip team={club} title={`${club} in ${p.season}`} />}
                      </span>
                    </td>
                    <td>{managerShort(p.gm)}</td>
                    <td>
                      {p.keeper && <span className="chip warning">keeper</span>}
                      {p.farm && <span className="chip">farm</span>}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        ) : (
          <>
            <div className="toolbar">
              <div className="segs scroll">
                {SEASONS.map((s) => (
                  <button key={s.season} type="button" className={`seg ${s.season === year ? 'on' : ''}`}
                    onClick={() => steer(() => setYear(s.season))}>{s.season}</button>
                ))}
              </div>
            </div>

            <section className="card histcard">
              <div className="histhead">
                <h3 className="striphead">{season.season} · {view === 'board' ? 'Board' : 'By team'}</h3>
                <div className="panes small">
                  <button type="button" className={`pane ${view === 'board' ? 'on' : ''}`}
                    onClick={() => steer(() => setView('board'))}>Board</button>
                  <button type="button" className={`pane ${view === 'team' ? 'on' : ''}`}
                    onClick={() => steer(() => setView('team'))}>By team</button>
                </div>
                {caveats.length > 0 && (
                  <span className="histcaveat" title="What the archive does not record for this season">
                    {caveats.join(' · ')}
                  </span>
                )}
                <span className="histcount">
                  {season.teams} teams · {season.rounds} rounds · {season.picks.length} picks
                </span>
              </div>

              {view === 'board' ? (
                <>
                  {/* 1400: the grid, every round, seats across. */}
                  {/*
                    HOW MANY COLUMNS IS THE SEASON'S OWN ANSWER (Tim, 8 September: "alignment on
                    the 2005 past draftboard needs to be fixed"). The grid was ten wide in the
                    stylesheet, and the league played 2005 with twelve teams — so seats 11 and 12
                    wrapped onto a line of their own under every round, and the two extra heads sat
                    under the first ten. Every other season has ten, which is why it went unseen.
                  */}
                  <div className="histgrid deskonly"
                    style={{ ['--seats' as string]: season.teams } as React.CSSProperties}>
                    <div className="hrow hhead">
                      <div />
                      {season.order.map((g, i) => (
                        <div className="hgm" key={`${g}${i}`}>
                          <b>{headFor(g, i)}</b>
                          {/* The era's own name for the seat, where the archive has it. */}
                          {teamFor(i) && teamFor(i) !== headFor(g, i)
                            && <i title={teamFor(i) ?? undefined}>{teamFor(i)}</i>}
                        </div>
                      ))}
                    </div>
                    {Array.from({ length: season.rounds }, (_, i) => i + 1).map((r) => {
                      /*
                       * A ROW IS SEATS, NOT PICKS (Tim, 8 September: "there are a couple drafts
                       * that had unpicked picks and messed up alignment (see 15.09 not aligned
                       * under 14.02 as it should be for 2003; and rounds 13-16 should be blank for
                       * Samant)").
                       *
                       * The row used to be whatever picks the archive holds for that round, laid
                       * out in order — so a round the sheet is missing a pick from came out one
                       * cell short and every pick after the gap slid a column to the left. 2003's
                       * round 15 drew 15.09 under seat 8, and 2022's four blank Samant slots
                       * dragged four rounds of the board out of true. The column is the seat, and
                       * a seat with no pick in it is a cell that says so.
                       */
                      const held = new Map(season.picks.filter((p) => p.round === r).map((p) => [p.pick, p]));
                      const cells = Array.from({ length: season.teams }, (_, j) => {
                        /* Column j is seat j+1; the snake decides which pick that seat made. */
                        const pick = r % 2 === 1 ? j + 1 : season.teams - j;
                        return held.get(pick)
                          ?? { round: r, pick, overall: (r - 1) * season.teams + pick,
                            gm: season.order[j] ?? '', player: '' };
                      });
                      return (
                        <div className="hrow" key={r}>
                          <div className="hrd"><b>{r}</b><i>{r % 2 === 1 ? '→' : '←'}</i></div>
                          {cells.map((p) => (
                            <div key={p.overall}
                              ref={p.overall === lit ? litCell : undefined}
                              className={`hcell dbcell ${p.player ? 'made' : 'empty'}`
                                + `${p.keeper ? ' keeper' : ''}${p.overall === lit ? ' lit' : ''}`}
                              /* Tinted by the man who drafted him, the way the live board tints by
                                 the man who owns the pick — identity, and nothing else. */
                              style={p.player
                                ? { background: `hsl(${hueOf(p.gm)} 50% 92.5%)`,
                                  ['--hue' as string]: hueOf(p.gm) } as React.CSSProperties
                                : undefined}>
                              {p.player ? cell(p) : <span className="bot"><span className="code">{code(p)}</span></span>}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                  {/* 390: one round as a list, the seat named on every row. */}
                  <div className="histlist phoneonly">
                    <div className="ppager">
                      <button type="button" className="linkbtn" disabled={round <= 1}
                        aria-label="Previous round" onClick={() => setRound((r) => Math.max(1, r - 1))}>‹</button>
                      <span className="pround">
                        <b>Round {round}</b>
                        <span className="mut dir">{round % 2 === 0 ? '← even' : 'odd →'}</span>
                      </span>
                      <button type="button" className="linkbtn" disabled={round >= season.rounds}
                        aria-label="Next round" onClick={() => setRound((r) => Math.min(season.rounds, r + 1))}>›</button>
                    </div>
                    <ul className="hrows">
                      {season.picks.filter((p) => p.round === round).sort((a, b) => a.overall - b.overall).map((p) => (
                        <li key={p.overall} className={`hlrow ${p.overall === lit ? 'lit' : ''}`}>
                          <span className="code">{code(p)}</span>
                          <span className="hseat">{season.gmFromSeatOnly ? seatOf(p.gm) : managerShort(p.gm)}</span>
                          {name(p)}
                          {p.keeper && <span className={`tcpill ${p.franchise ? 'tag' : p.farm ? 'farm' : ''}`}>{p.franchise ? 'Franchise' : p.farm ? 'Farm' : 'Keeper'}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              ) : (
                <div className="histteams">
                  {byGm.map(([g, picks], i) => (
                    <div className="hteam" key={g}>
                      <h4>{headFor(g, i)}<span className="mut">{picks.length}</span></h4>
                      <ol>
                        {picks.sort((a, b) => a.overall - b.overall).map((p) => (
                          <li key={p.overall} className={p.keeper ? 'keeper' : ''}>
                            <span className="code">{code(p)}</span>
                            {name(p)}
                            {p.keeper && <span className={`tcpill ${p.franchise ? 'tag' : p.farm ? 'farm' : ''}`}>{p.franchise ? 'Franchise' : p.farm ? 'Farm' : 'Keeper'}</span>}
                          </li>
                        ))}
                      </ol>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}

        <p className="note">{DATA.source} · read {DATA.readOn}</p>
      </Linkify>
    </div>
  );
}
